import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import Toggle from '@ui/Toggle';
import { CategoryDocument } from 'models/category';
import React from 'react';

const ShippingLabel = ({
  measurementSection,
  errors,
  form,
  register,
  handleWeightChange,
  toggleShipInsurance,
  insuranceEnabled,
  hideInsuranceOption,
  total,
  selectedCat,
  setError,
  clearErrors,
}: {
  measurementSection?: React.ReactElement;
  errors: any;
  form: any;
  register: any;
  handleWeightChange: (weight: number) => void;
  toggleShipInsurance: () => void;
  insuranceEnabled?: boolean;
  hideInsuranceOption?: boolean;
  total?: number;
  selectedCat?: CategoryDocument;
  setError?: any;
  clearErrors?: any;
}) => {
  const [weight, setWeight] = React.useState({
    pounds: 0,
    ounces: 0,
  });
  return (
    <div className="flex flex-col gap-12">
      <div className="flex flex-col gap-2">
        <h5 className="text-body-lg font-semibold">Package Details</h5>
        <p className="text-body-md text-text-muted">
          Enter the dimensions of the package you will be shipping. Be accurate
          - these will be used to create a shipping label.
        </p>
      </div>

      <div className="flex w-full flex-col-reverse gap-[1.6rem] lg:flex-row">
        {measurementSection && (
          <div className="flex lg:w-1/2">
            <div className="flex h-fit w-full flex-col overflow-clip rounded-2xl border border-[#d9d9d9]">
              <div className="px-8 py-6">
                <h5 className="text-body-md font-semibold">
                  Guide Measurements
                </h5>
                <p className="text-label text-text-muted">
                  Click to use its measurements
                </p>
              </div>
              {measurementSection}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-[1.6rem] lg:w-1/2">
          <div className="sm:gap-[2.4rem flex h-fit w-full flex-col gap-[1.6rem]">
            {errors?.shipment?.package?.weight?.value && (
              <p className="text-[1.3rem] font-semibold text-brand-red">
                {errors?.shipment?.package?.weight.value.message}
              </p>
            )}
            {errors?.shipment?.package?.weight && (
              <p className="text-[1.3rem] font-semibold text-brand-red">
                {errors?.shipment?.package?.weight.message}
              </p>
            )}

            <FormLabel value="Weight" required>
              <div className="flex gap-[1.6rem]">
                <FormInput
                  type="number"
                  placeholder="Pounds (*)"
                  unit="lb"
                  value={
                    Math.floor(
                      (form.shipment?.package?.weight?.value ?? 0) / 16
                    ) || ''
                  }
                  onChange={(e) => {
                    const pounds = parseInt(e.target.value) || 0;
                    const newWeight = {
                      ...weight,
                      pounds: pounds,
                    };
                    setWeight(newWeight);
                    const totalWeight = pounds * 16 + weight.ounces;
                    handleWeightChange(totalWeight);
                  }}
                />

                <FormInput
                  type="number"
                  placeholder="Ounces (*)"
                  unit="oz"
                  value={(form.shipment?.package?.weight?.value ?? 0) % 16}
                  onChange={(e) => {
                    const ounces = parseInt(e.target.value) || 0;
                    const newWeight = {
                      ...weight,
                      ounces: ounces,
                    };
                    setWeight(newWeight);
                    const totalWeight = weight.pounds * 16 + ounces;
                    handleWeightChange(totalWeight);
                  }}
                />
              </div>
            </FormLabel>
            <div>
              {errors?.shipment?.package?.dimensions?.length && (
                <p className="text-[1.3rem] font-semibold text-brand-red">
                  {errors.shipment.package.dimensions.length.message}
                </p>
              )}
              {errors?.shipment?.package?.dimensions?.width && (
                <p className="text-[1.3rem] font-semibold text-brand-red">
                  {errors.shipment.package.dimensions.width.message}
                </p>
              )}
              {errors?.shipment?.package?.dimensions?.height && (
                <p className="text-[1.3rem] font-semibold text-brand-red">
                  {errors.shipment.package.dimensions.height.message}
                </p>
              )}
            </div>

            <div className="flex flex-row gap-[1.6rem]">
              <FormLabel value="Length" required>
                <FormInput
                  type="number"
                  {...register('shipment.package.dimensions.length', {
                    valueAsNumber: true,
                    required: true,
                    onBlur: (e: any) => {
                      if (
                        selectedCat?.pkg_ranges?.length?.min &&
                        e.target.value < selectedCat?.pkg_ranges?.length?.min
                      ) {
                        setError?.('shipment.package.dimensions.length', {
                          message: `Length must be at least ${selectedCat?.pkg_ranges?.length?.min} inches`,
                        });
                      } else if (
                        selectedCat?.pkg_ranges?.length?.max &&
                        e.target.value > selectedCat?.pkg_ranges?.length?.max
                      ) {
                        setError?.('shipment.package.dimensions.length', {
                          message: `Length must be at most ${selectedCat?.pkg_ranges?.length?.max} inches`,
                        });
                      } else {
                        clearErrors?.('shipment.package.dimensions.length');
                      }
                    },
                  })}
                  error={!!errors?.shipment?.package?.dimensions?.length}
                  unit={'in'}
                />
              </FormLabel>

              <FormLabel value="Width" required>
                <FormInput
                  type="number"
                  {...register('shipment.package.dimensions.width', {
                    valueAsNumber: true,
                    required: true,
                    onBlur: (e: any) => {
                      if (
                        selectedCat?.pkg_ranges?.width?.min &&
                        e.target.value < selectedCat?.pkg_ranges?.width?.min
                      ) {
                        setError?.('shipment.package.dimensions.width', {
                          message: `Width must be at least ${selectedCat?.pkg_ranges?.width?.min} inches`,
                        });
                      } else if (
                        selectedCat?.pkg_ranges?.width?.max &&
                        e.target.value > selectedCat?.pkg_ranges?.width?.max
                      ) {
                        setError?.('shipment.package.dimensions.width', {
                          message: `Width must be at most ${selectedCat?.pkg_ranges?.width?.max} inches`,
                        });
                      } else {
                        clearErrors?.('shipment.package.dimensions.width');
                      }
                    },
                  })}
                  error={!!errors?.shipment?.package?.dimensions?.width}
                  unit={'in'}
                />
              </FormLabel>

              <FormLabel value="Height" required>
                <FormInput
                  type="number"
                  {...register('shipment.package.dimensions.height', {
                    valueAsNumber: true,
                    required: true,
                    onBlur: (e: any) => {
                      if (
                        selectedCat?.pkg_ranges?.height?.min &&
                        e.target.value < selectedCat?.pkg_ranges?.height?.min
                      ) {
                        setError?.('shipment.package.dimensions.height', {
                          message: `Height must be at least ${selectedCat?.pkg_ranges?.height?.min} inches`,
                        });
                      } else if (
                        selectedCat?.pkg_ranges?.height?.max &&
                        e.target.value > selectedCat?.pkg_ranges?.height?.max
                      ) {
                        setError?.('shipment.package.dimensions.height', {
                          message: `Height must be at most ${selectedCat?.pkg_ranges?.height?.max} inches`,
                        });
                      } else {
                        clearErrors?.('shipment.package.dimensions.height');
                      }
                    },
                  })}
                  error={!!errors?.shipment?.package?.dimensions?.height}
                  unit={'in'}
                />
              </FormLabel>
            </div>
          </div>
        </div>
      </div>

      {!hideInsuranceOption && (form.price === undefined || form.price > 0) && (
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-4">
            <span className="text-body-md font-semibold">
              {(form.shipment?.package?.insured_value?.amount ?? 0) > 0 ||
              !!insuranceEnabled ||
              form.price
                ? `Add Shipping Insurance for $${(
                    (form.price ?? total ?? 0) * 0.04
                  ).toFixed(2)}`
                : 'Add Shipping Insurance'}
            </span>
            <Toggle
              onChange={() => toggleShipInsurance()}
              enabled={
                (form.shipment?.package?.insured_value?.amount ?? 0) > 0 ||
                !!insuranceEnabled
              }
            />
          </div>
          <p className="text-body-md text-text-muted">
            <span className="font-semibold text-brand-secondary">
              Guarantee your payout if the package is lost or damaged
            </span>
            . We suggest insuring your package through Motorsport Locker
            Shipping Insurance, which costs 4% of the listed item price. This
            cost is deferred until the item is sold, and it will be deducted
            from your earnings. If you choose not to opt in, you will be
            responsible for addressing any shipping and handling issues that may
            arise.
          </p>
        </div>
      )}
    </div>
  );
};

export default ShippingLabel;
